import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Upload } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";

const DesignSystem = () => {
  const [pax, setPax] = useState(2);
  const maxPax = 8;
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [time, setTime] = useState<string>("");
  const [sliderValue, setSliderValue] = useState<number[]>([50]);
  const [switchOn, setSwitchOn] = useState(true);
  const [checked, setChecked] = useState(true);
  const [radio, setRadio] = useState("card");
  const [fileName, setFileName] = useState<string>("");

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  return (
    <div className="min-h-screen bg-background p-8 md:p-16 space-y-16">
      <div className="space-y-4">
        <h1 className="text-4xl font-heading font-bold text-foreground">
          Dibs design system
        </h1>
        <p className="text-lg text-muted-foreground font-body whitespace-nowrap">
          This is an interactive showcase of the core UI components and styling guidelines documented in <code className="bg-muted px-1 rounded">Design.md</code>.
        </p>
      </div>

      <section className="space-y-6">
        <h2 className="text-2xl font-heading font-semibold border-b pb-2">1. Typography</h2>
        <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
          <div>
            <h1 className="text-4xl font-heading font-bold">Heading 1 (Plus Jakarta Sans, bold, 36px)</h1>
            <p className="text-sm text-muted-foreground mt-1">class: `font-heading font-bold text-4xl`</p>
          </div>
          <div>
            <h2 className="text-2xl font-heading font-semibold">Heading 2 (Plus Jakarta Sans, semibold, 24px)</h2>
            <p className="text-sm text-muted-foreground mt-1">class: `font-heading font-semibold text-2xl`</p>
          </div>
          <div>
            <p className="text-base font-body text-foreground">Body text (Inter, regular, 16px). Used for general paragraphs, labels, and text across the application. The quick brown fox jumps over the lazy dog.</p>
            <p className="text-sm text-muted-foreground mt-1">class: `font-body text-base`</p>
          </div>
          <div>
            <p className="text-sm font-bold uppercase tracking-wider text-foreground">Section Header (Inter, bold, uppercase, 14px)</p>
            <p className="text-sm text-muted-foreground mt-1">class: `text-sm font-bold uppercase tracking-wider text-foreground` (Premium UI Style for section-level context)</p>
          </div>
          <div>
            <p className="text-sm font-body text-muted-foreground">Muted text (Inter, regular, 14px). Used for secondary text and descriptions.</p>
            <p className="text-sm text-muted-foreground mt-1">class: `font-body text-sm text-muted-foreground`</p>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-2xl font-heading font-semibold border-b pb-2">2. Color palette</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <div className="h-24 rounded-lg bg-primary shadow-sm flex items-end p-3">
              <span className="text-primary-foreground font-medium text-sm">Primary</span>
            </div>
            <p className="text-xs text-muted-foreground">hsl(13.9, 100%, 46.5%)</p>
          </div>
          <div className="space-y-2">
            <div className="h-24 rounded-lg bg-navy shadow-sm flex items-end p-3">
              <span className="text-white font-medium text-sm">Navy</span>
            </div>
            <p className="text-xs text-muted-foreground">hsl(222, 47%, 11%)</p>
          </div>
          <div className="space-y-2">
            <div className="h-24 rounded-lg bg-secondary border shadow-sm flex items-end p-3">
              <span className="text-secondary-foreground font-medium text-sm">Secondary</span>
            </div>
            <p className="text-xs text-muted-foreground">hsl(0, 0%, 97%)</p>
          </div>
          <div className="space-y-2">
            <div className="h-24 rounded-lg bg-background border shadow-sm flex items-end p-3">
              <span className="text-foreground font-medium text-sm">Background</span>
            </div>
            <p className="text-xs text-muted-foreground">hsl(0, 0%, 97%)</p>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-2xl font-heading font-semibold border-b pb-2">3. Buttons</h2>
        
        <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Variants</h3>
          <div className="flex flex-wrap gap-4">
            <Button variant="default">Default</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="link">Link</Button>
          </div>

          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider pt-4">Sizes</h3>
          <div className="flex flex-wrap items-center gap-4">
            <Button size="lg">Large Button</Button>
            <Button size="default">Default Button</Button>
            <Button size="sm">Small Button</Button>
            <Button size="icon">
              <span className="sr-only">Icon</span>
              <div className="w-4 h-4 rounded-full bg-current opacity-80" />
            </Button>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-2xl font-heading font-semibold border-b pb-2">4. Badges</h2>

        <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
          <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Variants</h3>
          <div className="flex flex-wrap gap-3">
            <Badge variant="default">Default badge</Badge>
            <Badge variant="secondary">Secondary badge</Badge>
            <Badge variant="destructive">Destructive badge</Badge>
            <Badge variant="outline">Outline badge</Badge>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-2xl font-heading font-semibold border-b pb-2">5. Cards & utilities</h2>
        
        <div className="grid md:grid-cols-2 gap-8">
          {/* Standard UI Card */}
          <Card className="hover:shadow-card-hover transition-shadow duration-300">
            <CardHeader>
              <CardTitle>Standard app card</CardTitle>
              <CardDescription>Using shadcn/ui card components</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                This utilizes `{"<CardHeader>"}`, `{"<CardTitle>"}`, `{"<CardContent>"}`, etc.
                Perfect for standard dashboard and app layouts. It also features the `.hover:shadow-card-hover` class on hover.
              </p>
            </CardContent>
            <CardFooter>
              <Button className="w-full">Action</Button>
            </CardFooter>
          </Card>

          {/* Landing Page Module Card */}
          <div className="module-card hover:-translate-y-1 transition-all duration-300">
            <h3 className="text-xl font-heading font-semibold mb-2">Module card (.module-card)</h3>
            <p className="text-muted-foreground text-sm mb-6">
              This card uses the custom `.module-card` utility class defined in `index.css`.
              It has a slightly rounded radius (`rounded-xl`) preferred for landing page aesthetics.
              Features a hover lift effect.
            </p>
            <div className="p-4 rounded-lg bg-secondary/50 text-center">
              <span className="gradient-text font-bold text-lg">Gradient text utility</span>
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-2xl font-heading font-semibold border-b pb-2">6. Writing style</h2>
        <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
          <p className="text-base font-body text-foreground">
            <span className="font-semibold">Always use sentence case</span> for all user-facing text — buttons, headers, titles, labels, navigation, toasts, and any other copy. Only the first word and proper nouns are capitalized.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2 p-4 rounded-lg border border-green-200 bg-green-50">
              <p className="text-xs font-semibold text-green-700 uppercase tracking-wider">Do</p>
              <ul className="space-y-1 text-sm font-body text-foreground">
                <li>Sign up</li>
                <li>Book a demo</li>
                <li>Forgot password?</li>
                <li>Admin dashboard</li>
                <li>Settings</li>
              </ul>
            </div>
            <div className="space-y-2 p-4 rounded-lg border border-red-200 bg-red-50">
              <p className="text-xs font-semibold text-red-700 uppercase tracking-wider">Don't</p>
              <ul className="space-y-1 text-sm font-body text-foreground line-through decoration-red-400/60">
                <li>Sign Up</li>
                <li>Book A Demo</li>
                <li>Forgot Password?</li>
                <li>Admin Dashboard</li>
                <li>SETTINGS</li>
              </ul>
            </div>
          </div>
          <p className="text-sm font-body text-muted-foreground">
            Exceptions: Acronyms and brand names keep their own capitalization (e.g., <span className="font-medium text-foreground">Dibs</span>, <span className="font-medium text-foreground">AI</span>, <span className="font-medium text-foreground">URL</span>, <span className="font-medium text-foreground">FAQ</span>, <span className="font-medium text-foreground">Continue with Google</span>), as well as <span className="font-medium text-foreground">uppercase section headers</span> defined in typography standards (e.g., <span className="font-medium text-foreground">STOREFRONT SETTINGS</span>, <span className="font-medium text-foreground">JUMP TO</span>).
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <h2 className="text-2xl font-heading font-semibold border-b pb-2">7. Form controls</h2>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Input */}
          <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Input</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ds-input">Standard input</Label>
                <Input id="ds-input" placeholder="Enter your text here..." />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ds-input-disabled">Disabled input</Label>
                <Input id="ds-input-disabled" disabled placeholder="Disabled input" />
              </div>
              <p className="text-xs text-muted-foreground">Pair every input with a label. White fill stands out against both gray and white surfaces.</p>
            </div>
          </div>

          {/* Textarea */}
          <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Textarea</h3>
            <div className="space-y-2">
              <Label htmlFor="ds-textarea">Notes</Label>
              <Textarea id="ds-textarea" placeholder="Add any notes for the host..." />
              <p className="text-xs text-muted-foreground">For longer freeform input — descriptions, messages, AI prompts.</p>
            </div>
          </div>

          {/* Select */}
          <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Select</h3>
            <div className="space-y-2">
              <Label htmlFor="ds-select">Time slot</Label>
              <Select value={time} onValueChange={setTime}>
                <SelectTrigger id="ds-select">
                  <SelectValue placeholder="Choose a time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="18:00">6:00 pm</SelectItem>
                  <SelectItem value="18:30">6:30 pm</SelectItem>
                  <SelectItem value="19:00">7:00 pm</SelectItem>
                  <SelectItem value="19:30">7:30 pm</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Use for 4+ mutually exclusive options. For 2–3, prefer tabs.</p>
            </div>
          </div>

          {/* Date picker */}
          <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Date picker</h3>
            <div className="space-y-2">
              <Label>Booking date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal bg-card hover:bg-card hover:text-foreground",
                      !date && "text-muted-foreground",
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} initialFocus className="w-full" />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">Outline button trigger + calendar inside a popover. Use date-fns `format()` for display.</p>
            </div>
          </div>

          {/* Quantity stepper */}
          <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Quantity stepper</h3>
            <div className="space-y-2">
              <Label>
                Pax
                <span className="text-xs text-muted-foreground ml-2 font-normal">
                  (max {maxPax})
                </span>
              </Label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPax(Math.max(1, pax - 1))}
                  className="h-10 w-10 shrink-0 rounded-md border border-input bg-card flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-50"
                  disabled={pax <= 1}
                  aria-label="Decrease"
                >
                  <span className="text-base font-medium">−</span>
                </button>
                <Input
                  type="number"
                  value={pax || ""}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (!isNaN(val)) {
                      setPax(Math.min(maxPax, Math.max(1, val)));
                    } else if (e.target.value === "") {
                      // @ts-expect-error - allow empty temporarily while typing
                      setPax("");
                    }
                  }}
                  onBlur={() => {
                    if (!pax || pax < 1) setPax(1);
                  }}
                  className="flex-1 text-center font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  type="button"
                  onClick={() => setPax(Math.min(maxPax, pax + 1))}
                  className="h-10 w-10 shrink-0 rounded-md border border-input bg-card flex items-center justify-center hover:bg-secondary transition-colors disabled:opacity-50"
                  disabled={pax >= maxPax}
                  aria-label="Increase"
                >
                  <span className="text-base font-medium">+</span>
                </button>
              </div>
              <p className="text-xs text-muted-foreground">For integer quantities with min/max. Users can type or use buttons. Disable buttons at bounds.</p>
            </div>
          </div>

          {/* Slider */}
          <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Slider</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Price ceiling</Label>
                <span className="text-sm font-medium text-foreground">${sliderValue[0]}</span>
              </div>
              <Slider value={sliderValue} onValueChange={setSliderValue} max={200} step={5} />
              <p className="text-xs text-muted-foreground">Always show the current value — sliders alone don't communicate exact state.</p>
            </div>
          </div>

          {/* Switch */}
          <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Switch</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="ds-switch">Public listing</Label>
                  <p className="text-xs text-muted-foreground">Visible to anyone with the link.</p>
                </div>
                <Switch id="ds-switch" checked={switchOn} onCheckedChange={setSwitchOn} />
              </div>
              <p className="text-xs text-muted-foreground pt-2">Binary toggle that takes effect immediately — no save button.</p>
            </div>
          </div>

          {/* Checkbox */}
          <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Checkbox</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Checkbox id="ds-check" checked={checked} onCheckedChange={(v) => setChecked(!!v)} className="mt-0.5" />
                <Label htmlFor="ds-check" className="font-normal leading-snug">
                  I agree to the terms and conditions
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">Use inside forms — commits on submit. For instant effect, use a switch.</p>
            </div>
          </div>

          {/* Radio group */}
          <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Radio group</h3>
            <div className="space-y-3">
              <Label>Payment method</Label>
              <RadioGroup value={radio} onValueChange={setRadio}>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="card" id="ds-radio-card" />
                  <Label htmlFor="ds-radio-card" className="font-normal">Credit or debit card</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="paynow" id="ds-radio-paynow" />
                  <Label htmlFor="ds-radio-paynow" className="font-normal">PayNow</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="cash" id="ds-radio-cash" />
                  <Label htmlFor="ds-radio-cash" className="font-normal">Pay at venue</Label>
                </div>
              </RadioGroup>
              <p className="text-xs text-muted-foreground">Use for 2–5 mutually exclusive options when all should be visible at once. For longer lists, use a select.</p>
            </div>
          </div>

          {/* File upload */}
          <div className="space-y-4 bg-card p-6 rounded-lg border shadow-sm">
            <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">File upload</h3>
            <div className="space-y-2">
              <Label htmlFor="ds-file">Cover image</Label>
              <label
                htmlFor="ds-file"
                className="flex flex-col items-center justify-center gap-2 w-full min-h-[120px] rounded-md border border-dashed border-input bg-card px-4 py-6 text-center cursor-pointer hover:bg-secondary transition-colors"
              >
                <Upload className="h-5 w-5 text-muted-foreground" />
                <div className="space-y-0.5">
                  <p className="text-sm font-medium text-foreground">
                    {fileName || "Click to upload or drag and drop"}
                  </p>
                  <p className="text-xs text-muted-foreground">PNG, JPG up to 5MB</p>
                </div>
                <input
                  id="ds-file"
                  type="file"
                  className="sr-only"
                  accept="image/png,image/jpeg"
                  onChange={(e) => setFileName(e.target.files?.[0]?.name ?? "")}
                />
              </label>
              <p className="text-xs text-muted-foreground">Dashed-border drop zone with hidden native input. Show filename after selection.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesignSystem;
